import { useEffect, useState, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Star, RefreshCw, AlertCircle } from 'lucide-react';
import { api } from '../../api/client';
import { useUIStore } from '../../stores/uiStore';

interface ReviewSummary {
  seller_id: number;
  item_id: string;
  catalog_product_id: string | null;
  rating_average: number;
  total_reviews: number;
  one_star: number;
  two_star: number;
  three_star: number;
  four_star: number;
  five_star: number;
  last_synced_at: string;
}

interface ReviewsEnvelope {
  success: boolean;
  data?: ReviewSummary[];
  error?: string;
}

interface CatalogTitle {
  item_id: string;
  title: string;
  thumbnail?: string;
}

function ratingColorClass(rating: number, total: number): string {
  if (total === 0) return 'text-[var(--color-text-muted)]';
  if (rating < 3) return 'text-red-600';
  if (rating < 4) return 'text-yellow-600';
  return 'text-green-600';
}

function StarRow({ rating, total }: { rating: number; total: number }) {
  if (total === 0) {
    return <span className="text-xs text-[var(--color-text-muted)]">Sin reseñas</span>;
  }
  const filled = Math.round(rating);
  const color = rating < 3 ? 'text-red-500' : rating < 4 ? 'text-yellow-500' : 'text-green-500';
  return (
    <div className="flex items-center gap-1">
      <div className="flex">
        {[1, 2, 3, 4, 5].map((i) => (
          <Star
            key={i}
            size={14}
            className={i <= filled ? `${color} fill-current` : 'text-gray-300'}
          />
        ))}
      </div>
      <span className={`text-xs font-semibold ${ratingColorClass(rating, total)}`}>
        {rating.toFixed(1)}
      </span>
    </div>
  );
}

function DistributionBar({ summary }: { summary: ReviewSummary }) {
  const total = summary.total_reviews;
  if (total === 0) return <span className="text-xs text-[var(--color-text-muted)]">—</span>;
  const bars = [
    { star: 5, count: summary.five_star, color: 'bg-green-500' },
    { star: 4, count: summary.four_star, color: 'bg-green-400' },
    { star: 3, count: summary.three_star, color: 'bg-yellow-400' },
    { star: 2, count: summary.two_star, color: 'bg-orange-400' },
    { star: 1, count: summary.one_star, color: 'bg-red-500' },
  ];
  return (
    <div className="flex flex-col gap-0.5 min-w-[160px]">
      {bars.map((b) => {
        const pct = total > 0 ? (b.count / total) * 100 : 0;
        return (
          <div key={b.star} className="flex items-center gap-1.5 text-[10px]">
            <span className="w-4 text-right text-[var(--color-text-muted)] font-mono">{b.star}★</span>
            <div className="flex-1 h-2 bg-gray-100 rounded overflow-hidden">
              <div className={`h-full ${b.color}`} style={{ width: `${pct}%` }} />
            </div>
            <span className="w-8 text-right text-[var(--color-text-muted)] font-mono">
              {pct.toFixed(0)}%
            </span>
          </div>
        );
      })}
    </div>
  );
}

export default function ReviewsPage() {
  const { sellerId, sellers } = useUIStore();
  const [order, setOrder] = useState<'lowest' | 'recent'>('lowest');
  const [minReviews, setMinReviews] = useState<number>(1);
  const [rows, setRows] = useState<ReviewSummary[]>([]);
  const [titles, setTitles] = useState<Record<string, CatalogTitle>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = (await api.get<ReviewSummary[]>(
        `/reviews/seller/${sellerId}/summary?limit=200&order=${order}`
      )) as ReviewsEnvelope;
      if (!res.success || !res.data) {
        setError(res.error || 'Error al cargar reseñas');
        setRows([]);
        setLoading(false);
        return;
      }
      setRows(res.data);

      try {
        const params = new URLSearchParams({
          seller_id: String(sellerId),
          page: '1',
          per_page: '500',
        });
        const tRes = await api.get<CatalogTitle[]>(`/catalog/products?${params}`);
        if (tRes.success && tRes.data) {
          const map: Record<string, CatalogTitle> = {};
          for (const p of tRes.data as CatalogTitle[]) {
            map[p.item_id] = p;
          }
          setTitles(map);
        }
      } catch {
        // títulos opcionales — no romper la página
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Fetch failed');
    } finally {
      setLoading(false);
    }
  }, [sellerId, order]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const filtered = useMemo(
    () => rows.filter((r) => r.total_reviews >= minReviews),
    [rows, minReviews]
  );

  const stats = useMemo(() => {
    const withReviews = rows.filter((r) => r.total_reviews > 0);
    const lowRating = withReviews.filter((r) => r.rating_average < 3);
    const noReviews = rows.filter((r) => r.total_reviews === 0);
    const totalReviews = withReviews.reduce((sum, r) => sum + r.total_reviews, 0);
    const avgRating =
      withReviews.length > 0
        ? withReviews.reduce((sum, r) => sum + r.rating_average * r.total_reviews, 0) /
          totalReviews
        : 0;
    return {
      lowRating: lowRating.length,
      noReviews: noReviews.length,
      avgRating,
      totalReviews,
    };
  }, [rows]);

  const currentSeller = sellers.find((s) => s.id === sellerId);

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text)]">Reseñas de productos</h1>
          <p className="text-sm text-[var(--color-text-muted)]">
            Vendedor: <strong>{currentSeller?.nickname || sellerId}</strong> · El cron
            sincroniza una vez por día
          </p>
        </div>
        <button
          onClick={fetchData}
          className="flex items-center gap-2 px-4 py-2 bg-[var(--color-primary)] text-white rounded hover:opacity-90"
        >
          <RefreshCw className="w-4 h-4" />
          Refrescar
        </button>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-3 items-end mb-6 bg-[var(--color-surface)] p-4 rounded-lg border border-[var(--color-border)]">
        <div>
          <label className="block text-xs font-semibold text-[var(--color-text-muted)] mb-1 uppercase">
            Orden
          </label>
          <select
            value={order}
            onChange={(e) => setOrder(e.target.value as 'lowest' | 'recent')}
            className="px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]"
          >
            <option value="lowest">Más bajos</option>
            <option value="recent">Más recientes</option>
          </select>
        </div>
        <div>
          <label className="block text-xs font-semibold text-[var(--color-text-muted)] mb-1 uppercase">
            Mín. reseñas
          </label>
          <input
            type="number"
            min={0}
            value={minReviews}
            onChange={(e) => setMinReviews(Math.max(0, parseInt(e.target.value, 10) || 0))}
            className="w-24 px-3 py-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] text-sm text-[var(--color-text)]"
          />
        </div>
      </div>

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4 text-center">
          <div className="text-2xl font-bold text-red-600">{stats.lowRating}</div>
          <div className="text-xs text-[var(--color-text-muted)] mt-1">
            Productos con rating menor a 3 estrellas
          </div>
        </div>
        <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4 text-center">
          <div className="text-2xl font-bold text-[var(--color-text-muted)]">
            {stats.noReviews}
          </div>
          <div className="text-xs text-[var(--color-text-muted)] mt-1">
            Productos sin reseñas todavía
          </div>
        </div>
        <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4 text-center">
          <div
            className={`text-2xl font-bold ${
              stats.avgRating >= 4
                ? 'text-green-600'
                : stats.avgRating >= 3
                  ? 'text-yellow-600'
                  : 'text-red-600'
            }`}
          >
            {stats.avgRating > 0 ? stats.avgRating.toFixed(2) : '—'}
          </div>
          <div className="text-xs text-[var(--color-text-muted)] mt-1">
            Rating promedio del vendedor
          </div>
        </div>
        <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] p-4 text-center">
          <div className="text-2xl font-bold text-[var(--color-text)]">
            {stats.totalReviews.toLocaleString()}
          </div>
          <div className="text-xs text-[var(--color-text-muted)] mt-1">
            Total de reseñas acumuladas
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-300 rounded p-4 text-red-800 mb-6 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          <span>{error}</span>
        </div>
      )}

      {/* Tabla */}
      <div className="bg-[var(--color-surface)] rounded-lg border border-[var(--color-border)] overflow-hidden">
        {loading ? (
          <div className="animate-pulse p-4 space-y-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-16 bg-[var(--color-border-light)] rounded" />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-[var(--color-text-muted)]">
            <Star size={48} className="opacity-20 mb-3" />
            <p>No hay reseñas capturadas todavía. El cron corre cada día.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-surface-hover)] text-xs uppercase text-[var(--color-text-secondary)]">
                <tr>
                  <th className="px-3 py-2.5 text-left">Producto</th>
                  <th className="px-3 py-2.5 text-left w-32">Item ID</th>
                  <th className="px-3 py-2.5 text-left w-40">Rating</th>
                  <th className="px-3 py-2.5 text-right w-24">Reseñas</th>
                  <th className="px-3 py-2.5 text-left w-56">Distribución</th>
                  <th className="px-3 py-2.5 text-left w-32">Último sync</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const t = titles[r.item_id];
                  return (
                    <tr
                      key={`${r.seller_id}-${r.item_id}`}
                      className="border-t border-[var(--color-border-light)] hover:bg-[var(--color-surface-hover)]"
                    >
                      <td className="px-3 py-2 max-w-md">
                        <Link
                          to={`/catalog/${r.item_id}`}
                          className="flex items-center gap-2 text-[var(--color-primary)] hover:underline"
                        >
                          {t?.thumbnail && (
                            <img
                              src={t.thumbnail}
                              alt=""
                              className="w-8 h-8 rounded object-contain bg-[var(--color-border-light)] shrink-0"
                              loading="lazy"
                            />
                          )}
                          <span className="line-clamp-2 text-sm">
                            {t?.title || <em className="text-[var(--color-text-muted)]">Sin título cacheado</em>}
                          </span>
                        </Link>
                      </td>
                      <td className="px-3 py-2 font-mono text-xs text-[var(--color-text-muted)]">
                        {r.item_id}
                      </td>
                      <td className="px-3 py-2">
                        <StarRow rating={r.rating_average} total={r.total_reviews} />
                      </td>
                      <td className="px-3 py-2 text-right font-mono">
                        {r.total_reviews.toLocaleString()}
                      </td>
                      <td className="px-3 py-2">
                        <DistributionBar summary={r} />
                      </td>
                      <td className="px-3 py-2 text-xs text-[var(--color-text-muted)]">
                        {r.last_synced_at
                          ? new Date(r.last_synced_at).toLocaleDateString()
                          : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="mt-4 text-xs text-[var(--color-text-muted)]">
        Endpoint: <code>GET /api/reviews/seller/{sellerId}/summary</code>
      </div>
    </div>
  );
}
